<template>
  <div
    class="fts-automation-node-sequence automation-node"
    :class="{
      'automation-node--debug': data.debug,
      'automation-node--debugging': isDebugging && !isActionDebug,
    }"
  >
    <div class="node-header">
      <div class="node-header__label">
        <i class="fts-icon-automation-action node-header-icon" />
        <div class="node-header-title">
          Actions
          <span v-if="data.actions && data.actions.length"
            >({{ data.actions && data.actions.length }})</span
          >
        </div>
      </div>
      <div class="node-header__action">
        <fts-automation-debugpoint
          v-model="data.debug"
          class="node-header__debugpoint"
          @input="updateData"
        />
      </div>
    </div>
    <!-- list -->
    <div class="node-sequence-list" v-if="data.actions && data.actions.length">
      <div
        v-for="(action, index) in data.actions"
        :key="action.id"
        class="node-sequence-border"
        :class="{
          'node-sequence-border--debugging': isActionDebugging(action),
        }"
        @click="openAction(action)"
        @mouseover="selectedActionIndex = index"
        @mouseleave="selectedActionIndex = -1"
      >
        <div class="node-sequence-wrapper">
          <div class="node-sequence__header">
            <div class="header__l">
              <div
                class="node-sequence-tag"
                v-if="selectedAction(action).variable"
              >
              <!-- <div>{{action.fieldName}}</div> -->
                <fts-automation-label
                  :label-name="action.name"
                  :label-type="action.variableType"
                  :is-list="action.isList"
                  class="node-sequence-tag__label"
                />
                <fts-automation-badge
                  :module-id="action.moduleId"
                  :activity-id="action.activityId"
                  :information-id="action.informationId"
                  class="node-sequence-tag__badge"
                />
              </div>
              <!-- set (assign) -->
              <div v-if="action.type === 'assign'">
                <fts-formula-display
                  v-model="action.variable"
                  :variables="data.variables"
                  class="node-sequence-formula-display"
                />
              </div>
              <!-- do (doActivity) -->
              <fts-automation-badge
                :module-id="action.moduleId"
                :activity-id="action.activityId"
                :information-id="action.informationId"
                :emoji-size="14"
                class="node-sequence-do-activity"
                v-else-if="action.type === 'doActivity'"
              />
              <!-- error (exception) -->
              <div
                class="node-sequence-exception"
                v-if="action.type === 'exception'"
              >
                Message
              </div>
            </div>
            <div class="header__r">
              <font-awesome-icon
                :icon="['far', 'trash-alt']"
                class="node-sequence-trash stopPropagation"
                @click="removeConfirmation(action)"
                v-if="selectedActionIndex === index"
              />
              <fts-automation-debugpoint
                v-model="action.debug"
                class="stopPropagation"
                v-if="selectedActionIndex === index || action.debug"
              />
              <div class="node-sequence-label">
                {{ selectedAction(action).name }}
              </div>
            </div>
          </div>
          <div class="node-sequence__content">
            <!-- set (assign) -->
            <template v-if="action.type === 'assign'">
              <div class="node-sequence-field-container">
                <fts-variable-assignment-display
                  :moduleId="action.moduleId"
                  :action="action"
                  :variables="data.variables"
                ></fts-variable-assignment-display>
              </div>
            </template>
            <!-- find, findAll -->
            <!-- <div
              style="margin-top: 0.375rem; font-size: 0.875rem"
              v-if="
                (action.type === 'find' || action.type === 'findAll') &&
                action.filter &&
                action.filter.items
              "
            >
              <fts-automation-query-display
                :items="action.filter.items"
                :state-source="stateSource"
              />
            </div> -->
            <!-- error (exception) -->
            <div
              class="node-sequence-expression"
              v-if="action.type === 'exception' && action.message"
            >
              <fts-formula-display
                v-model="action.message"
                class="node-sequence-formula-display"
                :variables="data.variables"
              />
            </div>
            <!-- do (doActivity) -->
            <div
              class="node-sequence-expression"
              v-if="action.type === 'doActivity' && action.entry"
            >
              <fts-formula-display
                v-model="action.entry"
                class="node-sequence-formula-display"
                :variables="data.variables"
              />
            </div>
            <!-- create (setVariable) -->
            <div
              class="node-sequence-expression"
              v-if="action.type === 'setVariable' && action.value"
            >
              <fts-formula-display
                v-model="action.value"
                class="node-sequence-formula-display"
                :variables="data.variables"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- action -->
    <div class="node-sequence-action">
      <fts-btn
        outline
        text-transform-init
        sm
        @click="openAction"
      >
        <font-awesome-icon :icon="['fas', 'plus']" /> Add Action
      </fts-btn>
    </div>
    <!-- automation action modal -->
    <fts-automation-action-modal ref="ftsAutomationActionModal" />
  </div>
</template>

<script>
import draggable from "vuedraggable";

import { automationDesignStore } from "../../store/automationDesignStore";
import commons from "../../commons/commons";
import automationBlocks from "../../commons/automationBlocks";

import FTSBtn from "../../elements/FTSBtn";
import FTSAutomationActionModal from "./FTSAutomationActionModal";
import FTSAutomationScopedVariables from "./FTSAutomationScopedVariables";
import FTSAutomationDebugpoint from "./FTSAutomationDebugpoint";
import FTSAutomationLabel from "./FTSAutomationLabel";
import FTSAutomationBadge from "./FTSAutomationBadge";
import FTSAutomationQueryDisplay from "./FTSAutomationQueryDisplay";
import FTSFormulaDisplay from "../FTSFormulaDisplay";
import FTSVariableAssignmentDisplay from "./FTSVariableAssignmentDisplay.vue";

export default {
  props: {
    data: Object,
  },
  data: () => ({
    selectedActionIndex: -1,
    isDebugging: false,
    isActionDebug: false,
  }),
  components: {
    draggable,
    "fts-btn": FTSBtn,
    "fts-automation-action-modal": FTSAutomationActionModal,
    "fts-automation-scoped-variables": FTSAutomationScopedVariables,
    "fts-automation-debugpoint": FTSAutomationDebugpoint,
    "fts-automation-label": FTSAutomationLabel,
    "fts-automation-badge": FTSAutomationBadge,
    "fts-automation-query-display": FTSAutomationQueryDisplay,
    "fts-formula-display": FTSFormulaDisplay,
    "fts-variable-assignment-display": FTSVariableAssignmentDisplay,
  },
  methods: {
    updateData() {
      this.$forceUpdate();
    },
    isActionDebugging(action) {
      if (this.isDebugging) {
        return automationDesignStore.state.debugPayload.actionId == action.id;
      }
    },
    async openAction(item) {
      if (commons.elementStopPropagation()) return;
      let innerVariables = this.computeVariable(item);
      /* action */
      let action = { ...item };
      if (!action.id) {
        action.id = commons.nanoID();
      }
      /* actions */
      if (!Array.isArray(this.data.actions)) {
        this.data.actions = [];
      }
      let actions = this.data.actions;

      const result = await this.$refs.ftsAutomationActionModal.show(
        action,
        innerVariables,
        this.data
      );

      if (result != null && result != true) {
        const findIndex = actions.findIndex((x) => x.id === result.id);
        if (findIndex > -1) 
          this.$set(actions, findIndex, result);
        else 
          actions.push(result);
        this.$forceUpdate();
        automationDesignStore.dispatch("updateVariables");
      } else if (result === true) {
        this.removeAction(action);
      }
    },
    computeVariable(item) {
      if (!this.data.actions) {
        return [...this.data.priorVariables];
      }

      let ind = this.data.actions.length;

      if (item && this.data.actions.indexOf(item) > -1) {
        ind = this.data.actions.indexOf(item);
      }

      let priors = this.data.actions
        .slice(0, ind)
        .filter(
          (x) =>
            automationBlocks.actionSelections.find((y) => y.id == x.type)
              .variable
        );

      return [...this.data.priorVariables, ...priors];
    },
    removeAction(item) {
      const findIndex = this.data.actions.findIndex((x) => x.id === item.id);
      if (findIndex > -1) {
        this.data.actions.splice(findIndex, 1);
        this.$forceUpdate();
        automationDesignStore.dispatch("updateVariables");
      } else {
        this.$toasted.global.error();
      }
    },
    removeConfirmation(item) {
      let message = item.name;
      if (!message) {
        const name = this.selectedAction(item).name;
        message = `action ${name}`;
      }
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${message}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.removeAction(item);
              this.$modal.hide("dialog");
            },
          },
        ],
      });
    },
    selectedAction(item) {
      return automationBlocks.actionSelections.find((x) => x.id == item.type);
    },
  },
  computed: {
    stateSource() {
      return this.$store.getters.workspaceStates;
    },
  },
  async mounted() {
    let vm = this;

    window.eventBus.$on("refreshAutomationBlocks", () => {
      vm.isDebugging = false;
      vm.isActionDebug = false;
      if (
        automationDesignStore.state.debugPayload &&
        automationDesignStore.state.debugPayload.nodeId
      ) {
        let nodeId = automationDesignStore.state.debugPayload.nodeId;
        let diagram = automationDesignStore.state.diagram;
        if (diagram[nodeId].data.id == vm.data.id) {
          vm.isDebugging = true;
          vm.isActionDebug = automationDesignStore.state.debugPayload.actionId;
        }
      }

      vm.$forceUpdate();
    });
  },
  beforeDestroy() {
    window.eventBus.$off("refreshAutomationBlocks");
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-automation-node-sequence {
  .node-sequence-list {
    & + .node-sequence-action {
      margin-top: 1rem;
    }
    .node-sequence-border {
      $border-m: 0.375rem;
      margin-left: $border-m;
      margin-right: $border-m;
      border: 1px solid $borderColor;
      border-radius: 0.75rem;
      cursor: pointer;
      &.node-sequence-border--debugging {
        border-width: 2px;
        border-color: $dangerColor;
      }
      &:not(:last-child) {
        margin-bottom: 0.375rem;
      }
      .node-sequence-wrapper {
        padding: 1rem - $border-m;
        .node-sequence__header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          .header__l {
            min-width: 0;
            flex: 1;
            margin-right: 0.75rem;
            .node-sequence-tag {
              max-width: 100%;
              display: inline-flex;
              flex-direction: column;
              padding: 0.175rem 0.575rem;
              border-radius: 0.5rem;
              border: 1px solid var(--fts-block-variable-border);
              background: var(--fts-block-variable-background);
              .node-sequence-tag__label {
                font-size: 0.875rem;
              }
              .node-sequence-tag__badge {
                font-size: 0.675rem;
              }
              .node-sequence-tag__label + .node-sequence-tag__badge {
                margin-top: 0.135rem;
              }
            }
            .node-sequence-do-activity {
              margin-top: 0.375rem;
              font-size: 0.75rem;
            }
            .node-sequence-exception {
              font-size: 0.875rem;
            }
          }
          .header__r {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            > *:not(:last-child) {
              margin-right: 0.75rem;
            }
            .node-sequence-label {
              font-size: 1rem;
            }
            .node-sequence-trash {
              font-size: 0.875rem;
              color: $grayLightColor;
            }
          }
        }
        .node-sequence__content {
          .node-sequence-field-container {
            margin-top: 0.375rem;
          }
          .node-sequence-expression {
            margin-top: 0.375rem;
          }
        }
        .node-sequence-formula-display {
          font-size: 0.875rem;
        }
      }
    }
  }
  .node-sequence-action {
    margin: 0 1rem 1rem;
    text-align: center;
    > .fts-btn {
      height: auto;
      padding: 0.75rem 2rem;
      border-radius: 0.75rem;
      font-weight: 400;
    }
  }
}
</style>